/**
 * Subscription Context
 * Manages subscription state and provides access to subscription features
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { backendApiService } from '../services/backendApiService'
import { supabase } from '../lib/supabase'
import { clientLogger } from '../utils/clientLogger'
import {
  SubscriptionDetails,
  UsageStats,
  SubscriptionTier,
  SubscriptionPlatform
} from '../types/subscription'

const FREE_TIER_SUBSCRIPTION: SubscriptionDetails = {
  tier: 'free' as SubscriptionTier,
  status: 'active',
  platform: null as SubscriptionPlatform,
  periodStart: null,
  periodEnd: null,
  trialStart: null,
  trialEnd: null,
  isInTrial: false,
  daysUntilRenewal: null,
  isPaidTier: false
}

const FREE_TIER_USAGE: UsageStats = {
  tier: 'free' as SubscriptionTier,
  usage: { mealPlansThisMonth: 0, globalChatThisWeek: 0, weeklyRerolls: { used: 0, limit: 5, weekStart: null } },
  limits: { mealPlansPerMonth: 5, globalChatPerWeek: 10, weeklyRerolls: 5 },
  remaining: { mealPlans: 5, globalChat: 10, rerolls: 5 }
}

interface SubscriptionContextType {
  subscription: SubscriptionDetails | null
  usage: UsageStats | null
  loading: boolean
  error: Error | null

  // Actions
  refreshSubscription: () => Promise<void>
  checkUsageLimit: (action: 'create_meal_plan' | 'global_chat_message') => Promise<boolean>

  /**
   * Optimistically increment the global weekly reroll counter by 1 without a
   * full network round-trip. Call this immediately after a reroll response
   * returns rerollTracking so the counter updates before refreshSubscription
   * completes.
   */
  optimisticIncrementRerolls: () => void

  // Platform management
  openPlatformPortal: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

interface SubscriptionProviderProps {
  children: ReactNode
}

export function SubscriptionProvider({ children }: SubscriptionProviderProps) {
  const [subscription, setSubscription] = useState<SubscriptionDetails | null>(null)
  const [usage, setUsage] = useState<UsageStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)

  // Subscribe directly to Supabase auth events so we react to TOKEN_REFRESHED
  // as well as INITIAL_SESSION. AuthContext.setAuthToken is synchronous and fires
  // at the top of onAuthStateChange before any async work, so by the time our
  // async fetch calls execute (next microtask tick) the token is already stored.
  useEffect(() => {
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED') {
          if (session) {
            refreshSubscription()
          } else {
            setSubscription(FREE_TIER_SUBSCRIPTION)
            setUsage(FREE_TIER_USAGE)
            setLoading(false)
          }
        } else if (event === 'SIGNED_OUT') {
          setSubscription(FREE_TIER_SUBSCRIPTION)
          setUsage(FREE_TIER_USAGE)
          setLoading(false)
        }
      }
    )
    return () => authSub.unsubscribe()
  }, [])

  /**
   * Immediately increment the global weekly reroll counter in local state
   * without waiting for a network round-trip. Safe to call concurrently with
   * refreshSubscription() — the full refresh will overwrite this patch once
   * it completes, which is fine because the server value is authoritative.
   */
  const optimisticIncrementRerolls = () => {
    setUsage(prev => {
      if (!prev) return prev
      const current = prev.usage.weeklyRerolls
      // Cap at the weekly limit so we never temporarily overshoot (e.g. 5→6 on
      // the 5th reroll), which would flash a false limit-reached state (bug #132).
      const weeklyLimit = current.limit ?? Infinity
      return {
        ...prev,
        usage: {
          ...prev.usage,
          weeklyRerolls: {
            ...current,
            used: Math.min(current.used + 1, weeklyLimit)
          }
        }
      }
    })
  }

  /**
   * Fetch subscription and usage data from API
   */
  const refreshSubscription = async () => {
    try {
      setLoading(true)
      setError(null)

      const [subData, usageData] = await Promise.all([
        backendApiService.getSubscription().catch(() => FREE_TIER_SUBSCRIPTION),
        backendApiService.getUsageStats().catch(() => FREE_TIER_USAGE)
      ])

      setSubscription(subData)
      setUsage(usageData)
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error fetching subscription')
      setError(err as Error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Check if user can perform action within usage limits
   * @param action - Action to check (create_meal_plan or global_chat_message)
   * @returns true if allowed, false if limit reached
   */
  const checkUsageLimit = async (action: 'create_meal_plan' | 'global_chat_message'): Promise<boolean> => {
    try {
      const result = await backendApiService.checkUsageLimit(action)
      return result.allowed
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error checking usage limit')
      // On error, allow action (fail open rather than blocking user)
      return true
    }
  }

  /**
   * Open platform-specific subscription management portal
   * - Stripe: Opens Stripe Customer Portal
   * - Apple: Opens App Store subscriptions
   * - Google: Opens Google Play subscriptions
   */
  const openPlatformPortal = async () => {
    if (!subscription) {
      clientLogger.warn({}, 'No subscription found, cannot open portal')
      return
    }

    try {
      switch (subscription.platform) {
        case 'stripe': {
          const { url } = await backendApiService.getStripePortalUrl()
          window.open(url, '_blank')
          break
        }

        case 'apple': {
          window.open('https://apps.apple.com/account/subscriptions', '_blank')
          break
        }

        case 'google': {
          window.open('https://play.google.com/store/account/subscriptions', '_blank')
          break
        }

        default: {
          clientLogger.warn({ platform: subscription.platform }, 'No platform found, cannot open portal')
          break
        }
      }
    } catch (err) {
      clientLogger.error({ err: err instanceof Error ? err : new Error(String(err)) }, 'Error opening platform portal')
      throw err
    }
  }

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        usage,
        loading,
        error,
        refreshSubscription,
        optimisticIncrementRerolls,
        checkUsageLimit,
        openPlatformPortal
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  )
}

/**
 * Hook to access subscription context
 * @throws Error if used outside SubscriptionProvider
 */
export function useSubscription() {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider')
  }
  return context
}

export default SubscriptionContext
